import { useQueries } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSearchParams } from 'next/navigation'
import type { CableProduct, LoadedPowerPlan, PowerFacts, PowerPlanSummary, RunSheet } from '@routr/core'
import type { DeviceDTO, DevicePaletteDTO, InventoryItemDTO, InviteDTO, MemberDTO, ProjectDetailDTO, ProjectSummaryDTO, WorkspaceDTO } from '@routr/shared'
import { CanvasWorkspace } from '@/components/canvas/CanvasWorkspace'
import { InventoryClient } from '@/components/inventory/InventoryClient'
import { LibraryClient } from '@/components/library/LibraryClient'
import { PowerIndexClient } from '@/components/power/PowerIndexClient'
import { PowerPlanClient } from '@/components/power/PowerPlanClient'
import { ProjectsClient } from '@/components/projects/ProjectsClient'
import { RunSheetView } from '@/components/run-sheet/RunSheetView'
import { LoadFailed, Loading, NotFound } from '@/components/shell/Loading'
import { SettingsClient } from '@/components/workspace/SettingsClient'
import { useApi, useMe } from '@/lib/api'
import { apiFetch } from '@/lib/client-api'
import { roleAtLeast } from '@/lib/roles'

/* ------------------------------------------------------------------ *
 * Each page fetches what v1's server component fetched, then renders the
 * same client component with the same props. The `*Client` components
 * re-fetch through `apiBase` and call router.refresh(), which the
 * navigation shim turns into a query invalidation.
 * ------------------------------------------------------------------ */

type Q<T> = { data?: T; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown }

/** Waits for every query; the first error wins; a 404 on the main one is the not-found page. */
function Gate<T extends Q<unknown>[]>({ queries, children }: { queries: [...T]; children: () => React.ReactNode }) {
  const failed = queries.find((q) => q.isError)
  if (failed) {
    const status = (failed.error as { status?: number }).status
    return status === 404 ? <NotFound /> : <LoadFailed error={failed.error} retry={() => void failed.refetch()} />
  }
  if (queries.some((q) => q.isPending || q.data === undefined)) return <Loading />
  return <>{children()}</>
}

const paths = (slug: string) => ({ basePath: `/w/${slug}`, apiBase: `/api/w/${slug}` })

export function ProjectsPage({ slug }: { slug: string }) {
  const projects = useApi<ProjectSummaryDTO[]>(`/api/w/${slug}/projects`)
  return <Gate queries={[projects]}>{() => <ProjectsClient initialProjects={projects.data!} {...paths(slug)} />}</Gate>
}

export function LibraryPage({ slug }: { slug: string }) {
  const params = useSearchParams()
  const me = useMe()
  const ws = useApi<WorkspaceDTO>(`/api/w/${slug}`)
  const devices = useApi<DeviceDTO[]>(`/api/w/${slug}/devices`)
  const inventory = useApi<InventoryItemDTO[]>(`/api/w/${slug}/inventory`)
  const favourites = useApi<string[]>(`/api/w/${slug}/favourites`)
  const brands = useApi<{ id: string | null; name: string; website: string | null; hasLogo: boolean; logoUrl: string | null }[]>('/api/brands')
  return (
    <Gate queries={[me, ws, devices, inventory, favourites, brands]}>
      {() => {
        const unitCounts: Record<string, number> = {}
        for (const item of inventory.data!) unitCounts[item.deviceId] = (unitCounts[item.deviceId] ?? 0) + 1
        const names = new Set(devices.data!.map((d) => d.manufacturer))
        return (
          <LibraryClient
            initialDevices={devices.data!}
            unitCounts={unitCounts}
            workspaceId={ws.data!.id}
            apiBase={`/api/w/${slug}`}
            inventoryPath={`/w/${slug}/inventory`}
            isPlatformAdmin={me.data!.isPlatformAdmin}
            brands={brands.data!.filter((b) => names.has(b.name))}
            initialFavouriteIds={favourites.data!}
            initialSelectedId={params.get('device') ?? undefined}
            initialEdit={params.get('edit') === '1'}
          />
        )
      }}
    </Gate>
  )
}

type Assignment = { projectId: string; projectName: string; label: string | null }

export function InventoryPage({ slug }: { slug: string }) {
  const devices = useApi<DevicePaletteDTO[]>(`/api/w/${slug}/devices?shape=palette`)
  const items = useApi<InventoryItemDTO[]>(`/api/w/${slug}/inventory`)
  const assignments = useApi<Record<string, Assignment[]>>(`/api/w/${slug}/inventory/assignments`)
  return (
    <Gate queries={[devices, items, assignments]}>
      {() => <InventoryClient devices={devices.data!} initialItems={items.data!} assignments={assignments.data!} {...paths(slug)} />}
    </Gate>
  )
}

export function SettingsPage({ slug }: { slug: string }) {
  const me = useMe()
  const ws = useApi<WorkspaceDTO>(`/api/w/${slug}`)
  const members = useApi<MemberDTO[]>(`/api/w/${slug}/members`)
  const canManage = ws.data ? roleAtLeast(ws.data.role, 'ADMIN') : false
  const invites = useApi<InviteDTO[]>(canManage ? `/api/w/${slug}/invites` : null)
  return (
    <Gate queries={[me, ws, members, ...(canManage ? [invites] : [])]}>
      {() => (
        <SettingsClient workspace={ws.data!} role={ws.data!.role} currentUserId={me.data!.id} initialMembers={members.data!} initialInvites={invites.data ?? []} apiBase={`/api/w/${slug}`} />
      )}
    </Gate>
  )
}

export function CanvasPage({ slug, id }: { slug: string; id: string }) {
  const ws = useApi<WorkspaceDTO>(`/api/w/${slug}`)
  const project = useApi<ProjectDetailDTO & { version: number }>(`/api/w/${slug}/projects/${id}`)
  const devices = useApi<DeviceDTO[]>(`/api/w/${slug}/devices`)
  const inventory = useApi<InventoryItemDTO[]>(`/api/w/${slug}/inventory`)
  const favourites = useApi<string[]>(`/api/w/${slug}/favourites`)
  const cables = useApi<CableProduct[]>(`/api/w/${slug}/cables`)
  return (
    <Gate queries={[ws, project, devices, inventory, favourites, cables]}>
      {() => {
        const placed = new Set(project.data!.nodes.map((n) => n.deviceId))
        const full = devices.data!
        return (
          <CanvasWorkspace
            project={project.data!}
            devices={full.map(toPalette)}
            placedDevices={full.filter((d) => placed.has(d.id))}
            inventory={inventory.data!}
            workspaceName={ws.data!.name}
            workspaceSlug={slug}
            cables={cables.data!}
            favouriteIds={favourites.data!}
            {...paths(slug)}
          />
        )
      }}
    </Gate>
  )
}

/** The palette shape, computed here from the full records the canvas already holds. */
function toPalette(d: DeviceDTO): DevicePaletteDTO {
  const inputs = d.ports.filter((p) => p.direction === 'INPUT').length
  const outputs = d.ports.filter((p) => p.direction === 'OUTPUT').length
  const bidirectional = d.ports.filter((p) => p.direction === 'BIDIRECTIONAL').length
  const base = [d.name, d.manufacturer, d.model, d.ipAddress ?? ''].join(' ').toLowerCase()
  const seen = new Set<string>()
  const ports: { text: string; label: string }[] = []
  for (const p of d.ports) {
    const label = `${p.name} · ${p.connector}`
    if (seen.has(label)) continue
    seen.add(label)
    ports.push({ text: label.toLowerCase(), label })
  }
  return {
    id: d.id,
    workspaceId: d.workspaceId,
    name: d.name,
    manufacturer: d.manufacturer,
    model: d.model,
    category: d.category,
    formFactorRU: d.formFactorRU,
    inputs,
    outputs,
    bidirectional,
    slots: d.slots.length,
    filledSlots: d.slots.filter((s) => s.card).length,
    search: { base, ports },
  }
}

type RunSheetPayload = { sheet: RunSheet; power: { id: string; vaRequired: number; unknownRows: number } | null }

export function RunSheetPage({ slug, id }: { slug: string; id: string }) {
  const sheet = useApi<RunSheetPayload>(`/api/w/${slug}/projects/${id}/run-sheet`)
  return <Gate queries={[sheet]}>{() => <RunSheetView sheet={sheet.data!.sheet} power={sheet.data!.power} {...paths(slug)} />}</Gate>
}

export function SharePage({ token }: { token: string }) {
  const sheet = useApi<RunSheetPayload>(`/api/share/${token}`)
  return (
    <Gate queries={[sheet]}>
      {() => <RunSheetView sheet={sheet.data!.sheet} power={sheet.data!.power} basePath="" apiBase="" shared />}
    </Gate>
  )
}

export function PowerIndexPage({ slug }: { slug: string }) {
  const params = useSearchParams()
  const navigate = useNavigate()
  const plans = useApi<PowerPlanSummary[]>(`/api/w/${slug}/power`)
  const projects = useApi<ProjectSummaryDTO[]>(`/api/w/${slug}/projects`)
  const seedProjectId = params.get('from')
  return (
    <Gate queries={[plans, projects]}>
      {() => {
        // Arriving from a canvas: straight to that project's plan if it has one.
        const existing = seedProjectId ? plans.data!.find((p) => p.projectId === seedProjectId) : undefined
        if (existing) {
          void navigate({ to: `/w/${slug}/power/${existing.id}` as never, replace: true })
          return <Loading />
        }
        return (
          <PowerIndexClient
            initialPlans={plans.data!}
            seedProjectId={seedProjectId}
            projects={projects.data!.map((p) => ({ id: p.id, name: p.name, nodeCount: p.nodeCount }))}
            {...paths(slug)}
          />
        )
      }}
    </Gate>
  )
}

export function PowerPlanPage({ slug, id }: { slug: string; id: string }) {
  const plan = useApi<LoadedPowerPlan>(`/api/w/${slug}/power/${id}`)
  const facts = useApi<PowerFacts[]>(`/api/w/${slug}/power-facts`)
  const [canvas, palette] = useQueries({
    queries: [
      { queryKey: ['api', plan.data?.projectId ? `/api/w/${slug}/projects/${plan.data.projectId}` : null], queryFn: () => apiFetch<ProjectDetailDTO>(`/api/w/${slug}/projects/${plan.data!.projectId}`), enabled: Boolean(plan.data?.projectId) },
      { queryKey: ['api', plan.data?.projectId ? `/api/w/${slug}/devices?shape=palette` : null], queryFn: () => apiFetch<DevicePaletteDTO[]>(`/api/w/${slug}/devices?shape=palette`), enabled: Boolean(plan.data?.projectId) },
    ],
  })
  const linked = Boolean(plan.data?.projectId)
  return (
    <Gate queries={[plan, facts, ...(linked ? [canvas, palette] : [])]}>
      {() => {
        const byId = new Map((palette.data ?? []).map((d) => [d.id, d]))
        const project = linked && canvas.data ? canvas.data : null
        return (
          <PowerPlanClient
            plan={plan.data!}
            devices={facts.data!}
            canvas={
              project
                ? {
                    id: project.id,
                    name: project.name,
                    nodes: project.nodes.map((node) => {
                      const d = byId.get(node.deviceId)
                      return { id: node.id, deviceId: node.deviceId, label: node.label ?? (d ? `${d.manufacturer} ${d.model}` : node.deviceId) }
                    }),
                  }
                : null
            }
            {...paths(slug)}
          />
        )
      }}
    </Gate>
  )
}
